import { TableSegmentBreakdownCell } from '@/features/shared/components/tableSystem/TableSegmentBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { getDetailLabel } from '@/shared/copy/details';
import { uiTokens } from '@/theme/uiTokens';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { TableTextCell } from '@/ui/composites/table';
import type { ProjectSummaryRow } from '@/features/projects/components/ProjectsTable.types';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { ProjectSummaryDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type StatusSegment = { label: string; value: number; color: string };

type BuildProjectsTableColumnsInput = {
  summariesByProjectId: Map<string, ProjectSummaryDto>;
  onSelectProject: (projectId: string) => void;
};

const columnFlex = { name: 1, releases: 1, features: 1, suites: 1, tests: 1 };

const buildStatusSegmentRows = (
  segments: StatusSegment[],
): Array<{ key: string; label: string; count: number; color: string }> =>
  segments.map((segment) => ({
    key: segment.label,
    label: `${segment.value.toLocaleString()} ${segment.label.toLowerCase()}`,
    count: segment.value,
    color: segment.color,
  }));

const renderSegmentBreakdown = ({
  entity,
  segments,
  total,
}: {
  entity: 'feature' | 'release';
  segments: StatusSegment[];
  total: number;
}): ReactElement => (
  <TableSegmentBreakdownCell
    totalLabel={`${total} ${entity}${total === 1 ? '' : 's'}`}
    rows={buildStatusSegmentRows(segments)}
  />
);

const renderLatestStatusBreakdown = ({
  entity,
  summary,
}: {
  entity: 'suite' | 'test';
  summary: ProjectSummaryDto | undefined;
}): ReactElement => {
  const prefix = entity === 'suite' ? 'suiteLatestRun' : 'testLatestRun';

  return (
    <TableStatusBreakdownCell
      entity={entity}
      counts={{
        passed: summary?.[`${prefix}Passed`] ?? 0,
        failed: summary?.[`${prefix}Failed`] ?? 0,
        errored: summary?.[`${prefix}Errored`] ?? 0,
        skipped: summary?.[`${prefix}Skipped`] ?? 0,
        untested: summary?.[`${prefix}Untested`] ?? 0,
      }}
    />
  );
};

export const buildProjectsTableColumns = ({
  summariesByProjectId,
  onSelectProject,
}: BuildProjectsTableColumnsInput): Array<GridColDef<ProjectSummaryRow>> => {
  const stringOperators = getStringFilterOperators();
  const nameColumn: GridColDef<ProjectSummaryRow> = {
    field: 'name',
    headerName: getDetailLabel('project'),
    flex: columnFlex.name,
    cellClassName: 'table-name-cell',
    filterOperators: stringOperators,
    renderCell: (params) => (
      <TableTextCell
        primary={{
          label: params.row.name,
          href: `/projects/${params.row.slug}`,
          onClick: () => {
            onSelectProject(params.row.id);
          },
        }}
      />
    ),
  };
  const releasesColumn: GridColDef<ProjectSummaryRow> = {
    field: 'releaseSummary',
    headerName: 'Releases',
    flex: columnFlex.releases,
    align: 'center',
    headerAlign: 'center',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['releaseSummary']>,
    ) => {
      const summary = summariesByProjectId.get(params.row.id)?.releaseSummary ?? {
        total: 0,
        draft: 0,
        planned: 0,
        overdue: 0,
        ready: 0,
        released: 0,
      };
      const segments: StatusSegment[] = [
        { label: 'Draft', value: summary.draft, color: uiTokens.statusColors.neutral },
        { label: 'Planned', value: summary.planned, color: uiTokens.statusColors.blue },
        { label: 'Ready', value: summary.ready, color: uiTokens.statusColors.orange },
        { label: 'Released', value: summary.released, color: uiTokens.statusColors.green },
        { label: 'Overdue', value: summary.overdue, color: uiTokens.statusColors.red },
      ];

      return renderSegmentBreakdown({ entity: 'release', segments, total: summary.total });
    },
  };
  const featuresColumn: GridColDef<ProjectSummaryRow> = {
    field: 'featureSummary',
    headerName: 'Features',
    flex: columnFlex.features,
    align: 'center',
    headerAlign: 'center',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['featureSummary']>,
    ) => {
      const summary = summariesByProjectId.get(params.row.id)?.featureSummary ?? {
        total: 0,
        draft: 0,
        planned: 0,
        ready: 0,
        released: 0,
      };
      const segments: StatusSegment[] = [
        { label: 'Draft', value: summary.draft, color: uiTokens.statusColors.neutral },
        { label: 'Planned', value: summary.planned, color: uiTokens.statusColors.blue },
        { label: 'Ready', value: summary.ready, color: uiTokens.statusColors.orange },
        { label: 'Released', value: summary.released, color: uiTokens.statusColors.green },
      ];

      return renderSegmentBreakdown({ entity: 'feature', segments, total: summary.total });
    },
  };
  const suitesColumn: GridColDef<ProjectSummaryRow> = {
    field: 'suiteSummary',
    headerName: 'Latest Suites',
    flex: columnFlex.suites,
    align: 'center',
    headerAlign: 'center',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['suiteSummary']>,
    ) => {
      const summary = summariesByProjectId.get(params.row.id);

      return renderLatestStatusBreakdown({ entity: 'suite', summary });
    },
  };
  const testsColumn: GridColDef<ProjectSummaryRow> = {
    field: 'testSummary',
    headerName: 'Latest Tests',
    flex: columnFlex.tests,
    align: 'center',
    headerAlign: 'center',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (
      params: GridRenderCellParams<ProjectSummaryRow, ProjectSummaryRow['testSummary']>,
    ) => {
      const summary = summariesByProjectId.get(params.row.id);

      return renderLatestStatusBreakdown({ entity: 'test', summary });
    },
  };

  return [nameColumn, releasesColumn, featuresColumn, suitesColumn, testsColumn];
};
